import { useQuery } from '@apollo/client/react'
import { useMemo, useState } from 'react'
import { useDebounce } from 'react-use'

import {
  buildCircleParentById,
  type CircleNode,
  getCirclesNotGroupedUnderNetworks,
  getFilteredCircles,
  getGroupedNetworkCircles,
} from '@/components/molecules/Menu/UserMenuContent/userMenuContent.model'
import {
  SearchCircleDocument,
  SearchCircleQuery,
  SearchCircleQueryVariables,
} from '@/graphql/api/generated'

import type { UserMenuContentProps } from '../UserMenuContent.types'

export const CIRCLE_FILTER_THRESHOLD = 5
export const CIRCLE_LIST_MAX_HEIGHT = '320px'
export const NETWORK_EXPAND_BUTTON_SIZE = 6

export function useUserMenuContextSwitchState({
  circles,
  networks,
  user,
}: Pick<UserMenuContentProps, 'circles' | 'networks'> & {
  /** Nullable: the rail's head calls this above its own `if (!user)` guard, as hooks demand. */
  user: UserMenuContentProps['user'] | null
}) {
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [expandedNetworks, setExpandedNetworks] = useState<
    Record<string, boolean>
  >({})
  const shouldEnableCircleFilter = circles.length > CIRCLE_FILTER_THRESHOLD

  useDebounce(
    () => {
      setDebouncedSearchTerm(searchTerm.trim())
    },
    250,
    [searchTerm]
  )

  const { data: searchedCirclesData } = useQuery<
    SearchCircleQuery,
    SearchCircleQueryVariables
  >(SearchCircleDocument, {
    skip: !shouldEnableCircleFilter || debouncedSearchTerm.length === 0,
    variables: {
      searchTerms: debouncedSearchTerm,
      first: 200,
    },
  })

  const filteredCircles = useMemo(() => {
    const matchedCircleIds = new Set(
      searchedCirclesData?.searchCircles?.edges
        ?.map((edge) => edge?.node?.id)
        .filter((id): id is string => Boolean(id)) ?? []
    )

    return getFilteredCircles({
      circles,
      shouldEnableCircleFilter,
      debouncedSearchTerm,
      matchedCircleIds,
    })
  }, [
    circles,
    debouncedSearchTerm,
    searchedCirclesData,
    shouldEnableCircleFilter,
  ])

  const circlesBySlug = useMemo(
    () =>
      new Map<string, CircleNode>(
        circles.map((circle: CircleNode) => [circle.slug, circle])
      ),
    [circles]
  )

  const authorizedContextsTree =
    user?.authorizedContextsTree ?? user?.treeCircles

  const groupedNetworkCircles = useMemo(
    () =>
      getGroupedNetworkCircles({
        networks,
        circlesBySlug,
        rawTree: authorizedContextsTree,
        debouncedSearchTerm,
      }),
    [networks, circlesBySlug, authorizedContextsTree, debouncedSearchTerm]
  )

  const hasNetworkGroups = groupedNetworkCircles.length > 0

  const standaloneContextCircles = useMemo(
    () =>
      getCirclesNotGroupedUnderNetworks({
        filteredCircles,
        groupedNetworkCircles,
      }),
    [filteredCircles, groupedNetworkCircles]
  )

  const visibleStandaloneCircleIds = useMemo(
    () => new Set(standaloneContextCircles.map((circle) => circle.id)),
    [standaloneContextCircles]
  )

  const visibleFilteredCircleIds = useMemo(
    () => new Set(filteredCircles.map((circle) => circle.id)),
    [filteredCircles]
  )

  const circleParentById = useMemo(
    () => buildCircleParentById({ circles, rawTree: authorizedContextsTree }),
    [circles, authorizedContextsTree]
  )

  const toggleNetworkExpansion = (networkId: string) => {
    setExpandedNetworks((prev) => ({
      ...prev,
      [networkId]: !prev[networkId],
    }))
  }

  return {
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm,
    expandedNetworks,
    toggleNetworkExpansion,
    shouldEnableCircleFilter,
    filteredCircles,
    groupedNetworkCircles,
    hasNetworkGroups,
    standaloneContextCircles,
    visibleStandaloneCircleIds,
    visibleFilteredCircleIds,
    circleParentById,
  }
}
